// NOTE: NSAI-5810 — why a turn failed, in terms a CLIENT can act on. Keep in
// sync with server/src/chat/types/generation-failure.types.ts.
//
// Each case is a failure whose own wording is unusable (V8 wrote one, a chat
// template the other) yet whose detail is safe to show, because it is produced
// by our code and carries none of the provider text the server's sanitizers
// exist to strip.
export enum EGenerationFailureKind {
  RUNTIME_FAULT = "runtime_fault",
  // NOTE: NSAI-6405 — the model was handed tools, dispatched none, and wrote its
  // chat template's tool-call markup into the reply as text. Nothing executes
  // that, so the card says the model cannot call tools instead of leaving the
  // user with an empty turn and no reason for it.
  TOOL_CALLS_UNSUPPORTED = "tool_calls_unsupported",
}

export interface IGenerationFailureErrorMetadata {
  kind: EGenerationFailureKind;
  // NOTE: Whether resending the same turn could plausibly succeed. A crash is
  // not a verdict on the request, so the card offers a way forward instead of a
  // dead end.
  isRetryable: boolean;
  // NOTE: The engine's own "Name: message", for the card's collapsed
  // disclosure — never the text the card leads with.
  technicalDetail: string;
}

// NOTE: The copy keys as LITERAL unions rather than `string`. Both clients type
// `t()` against their generated resource keys, so a literal here is what lets
// `t(view.titleKey)` compile — and it makes a key that does not exist in
// chat.json a compile error on web and mobile instead of an untranslated key
// rendered to a user. Mirrors the `generationFailure.*` block in
// i18n/locales/*/chat.json.
export type TGenerationFailureTitleKey =
  | "generationFailure.runtimeFault.title"
  | "generationFailure.toolCallsUnsupported.title"
  | "generationFailure.unknown.title";

export type TGenerationFailureBodyKey =
  | "generationFailure.runtimeFault.body"
  | "generationFailure.toolCallsUnsupported.body"
  | "generationFailure.unknown.body";

// NOTE: What a client needs to RENDER the failure, once the raw payload has been
// lifted: the copy is resolved by the caller's own `t` (web and mobile each hold
// their own i18n instance over the same shared keys), so the lift returns the
// keys plus the two decisions that drive the card's controls.
export interface IGenerationFailureView {
  kind: EGenerationFailureKind;
  titleKey: TGenerationFailureTitleKey;
  bodyKey: TGenerationFailureBodyKey;
  isRetryable: boolean;
  technicalDetail: string;
}
